import { useSelector } from 'react-redux';
import { AppState } from '../../../client-redux/reducers';
import { Language } from '../../../languages/languageHandler';
import Background from '../../../components/util/background';
import { Container, Grid, GridCol, Title } from '@mantine/core';
import Header from '../../../components/util/header';
import { getEquipmentStateLabel } from '../../../utils/script';
import { ICurrency } from '../../../../libraries/currencies';
import DashboardCurrencyTable from './dashboard-currency-table';
import { useEffect, useRef, useState } from 'react';
import { fetchEquipmentDashboard2Info, IDashboardEquipmentV2 } from '../../../../libraries/dashboardV2';
import { useParams } from 'react-router-dom';
import { LoginState } from '../../../client-redux/login/reducer';
import DashboardSafeTable from './dashboard-safe-table';
import DashboardCoinsRecyclerTable from './reciclers/dashboard-coins-recycler-table';
import DashboardNotesRecyclerTable from './reciclers/dashboard-notes-recycler-table';
import LoadingData from '../../../components/loading-data';
import DisplayField from '../../../components/display-field';
import DashboardPeriodField from './dashboard-period';
import DashboardPeriodDispensers from './dashboard-period-dispensers';

const EquipmentDashboard = () => {
  const lang = useSelector<AppState, Language>(state => state.lang.lang);
  const loginInfo = useSelector<AppState, LoginState>(state => state.login);
  const [equipmentInfo, setEquipmentInfo] = useState<IDashboardEquipmentV2>();
  const [loading, setLoading] = useState(false);
  const [noResults, setNoResults] = useState<boolean>(false);
  const [, /* statusOperCode */ setStatusOperCode] = useState<number | undefined>();
  const [isDeposit, setIsDesposit] = useState<boolean>(false);
  const controller = useRef(new AbortController()).current;
  const currencies = useSelector<AppState, ICurrency[]>(state => state.currencies.currencies);
  const [currencyMatch, setCurrencyMatch] = useState<ICurrency>();
  const { eid } = useParams<string>();

  useEffect(() => {
    getEquipmentDashboardInfo();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getEquipmentDashboardInfo = () => {
    if (!loginInfo.token) {
      return;
    }
    if (loading) {
      setNoResults(false);
      controller.abort();
    }
    setLoading(true);
    fetchEquipmentDashboard2Info(Number(eid))
      .then(response => {
        if (response.statusOper.code === 0) {
          processEquipmentInfo(response);
          setNoResults(false);
        } else {
          setStatusOperCode(response.statusOper.code);
          setNoResults(true);
        }
      })
      .catch(error => {
        if (error.name === 'AbortError') {
        } else {
          console.error('API request error:', error);
        }
      })
      .finally(() => setLoading(false));
  };

  const processEquipmentInfo = (equiInfo: IDashboardEquipmentV2) => {
    setEquipmentInfo(equiInfo);

    if (!equiInfo) return;

    const isDep = equiInfo.equiType.toString().startsWith('4');
    setIsDesposit(isDep);

    const periodCid = equiInfo.period?.totals?.[0]?.cid;

    if (periodCid) {
      setCurrencyMatch(currencies.find(c => c.cid === periodCid));
      return;
    }

    const firstExistence = equiInfo.existences?.[0];
    if (firstExistence) {
      setCurrencyMatch(currencies.find(c => c.cid === firstExistence.cid));
    }
  };

  if (noResults) {
    return (
      <Background>
        <Container>
          <Title order={1}>{lang.GLOBAL_NO_RESULTS}</Title>
        </Container>
      </Background>
    );
  }

  if (loading) {
    return <LoadingData text={lang.GLOBAL_FETCH_DATA} size={60} />;
  }

  return (
    <div className="dashboard-equipment-container">
      <Header
        pageTitle={`${lang.GLOBAL_EQUIPMENT} ${equipmentInfo?.eid} ${equipmentInfo?.description ? `- ${equipmentInfo.description}` : ''}`}
      />

      <Grid mt="xl">
        <GridCol span={{ base: 12, xs: 12, sm: 6, md: 3 }}>
          <DisplayField
            title={lang.EQUIPMENT_LOCAL}
            content={<>{equipmentInfo?.local ? equipmentInfo?.local.name : '-'}</>}
          />
        </GridCol>

        <GridCol span={{ base: 12, xs: 12, sm: 6, md: 3 }}>
          <DisplayField
            title={lang.EQUIPMENT_COM_STATE}
            content={equipmentInfo?.state ? getEquipmentStateLabel(equipmentInfo.state, lang) : '-'}
          />
        </GridCol>

        <GridCol span={{ base: 12, xs: 12, sm: 6, md: 3 }}>
          <DisplayField title={lang.EQUIPMENT_SERIAL_NUMBER} content={<>{equipmentInfo?.sn}</>} />
        </GridCol>

        <GridCol span={{ base: 12, xs: 12, sm: 6, md: 3 }}>
          <DisplayField
            title={lang.GLOBAL_EQUIPMENT_TYPE}
            content={<>{`${equipmentInfo?.equiType} - ${isDeposit ? lang.OP_8_LABEL : lang.OP_1_LABEL}`}</>}
          />
        </GridCol>
      </Grid>

      <Grid mb="xl">
        <>
          {equipmentInfo && equipmentInfo?.period && (
            <GridCol span={{ base: 12, xs: 12, sm: 6, md: 4 }}>
              <DashboardPeriodField
                period={equipmentInfo.period}
                totalEquipment={equipmentInfo?.existences ? equipmentInfo?.existences[0].totalAmount : 0}
                currencyMatch={currencyMatch!}
                isDeposit={isDeposit}
              />
            </GridCol>
          )}

          {equipmentInfo?.existences && equipmentInfo?.period && isDeposit && (
            <>
              {equipmentInfo.existences.map((existence, index) => (
                <GridCol span={{ base: 12, xs: 12, sm: 6, md: 4 }} key={index}>
                  <DashboardCurrencyTable lang={lang} currencyInfo={existence} hideCoins={equipmentInfo.eid === 10} />
                </GridCol>
              ))}
            </>
          )}

          {!isDeposit && (
            <>
              {equipmentInfo?.existences && currencyMatch && (
                <>
                  <GridCol span={{ base: 12, xs: 12, sm: 6, md: 4 }}>
                    <DashboardSafeTable currencyInfo={equipmentInfo.existences[0]} currencyMatch={currencyMatch} />
                  </GridCol>

                  {equipmentInfo.equiType !== 674 && (
                    <GridCol span={{ base: 12, xs: 12, sm: 6, md: 4 }}>
                      <DashboardCoinsRecyclerTable
                        currencyInfo={equipmentInfo.existences[0]}
                        currencyMatch={currencyMatch}
                      />
                    </GridCol>
                  )}

                  {equipmentInfo.equiType !== 674 && (
                    <GridCol span={{ base: 12, xs: 12, sm: 6, md: 4 }}>
                      <DashboardNotesRecyclerTable
                        currencyInfo={equipmentInfo.existences[0]}
                        currencyMatch={currencyMatch}
                      />
                    </GridCol>
                  )}
                </>
              )}
            </>
          )}

          {equipmentInfo?.dispensers && equipmentInfo.dispensers.length > 0 && (
            <GridCol span={{ base: 12, xs: 12, sm: 6, md: 4 }}>
              <DashboardPeriodDispensers dispensers={equipmentInfo.dispensers || []} />
            </GridCol>
          )}
        </>
      </Grid>
    </div>
  );
};

export default EquipmentDashboard;
